import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useMemo, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Pie } from "react-chartjs-2";
import Swal from "sweetalert2";
import plimsologo from "../../images/plimsologo.jpg";
import "./DataTable.css";
// import html2pdf from "html2pdf.js/dist/html2pdf.min";

ChartJS.register(ArcElement, Tooltip, Legend);

const DataTable = () => {
  const contentRef = useRef(null);

  const [inputData, setInputData] = useState({
    teamMembers: 20,
    daysInMonth: 24,
    coordinatorCost: 75,
    manpowerCost: 85,
    coordinators: 2,
    goPLIMSOLicenseCost: 65,
    deadLineLost: 100000,
  });

  const [spendingHours, setSpendingHours] = useState({
    ConstructionSequence: 4,
    CandQSequence: 3,
    Documentation: 4,
    dailyTeamMeetings: 1,
    CoordinationAndTool: 1,
  });

  const [savingPercentage, setSavingPercentage] = useState({
    ConstructionSequence: 0.3,
    CandQSequence: 0.4,
    Documentation: 0.5,
    dailyTeamMeetings: 0.3,
    CoordinationAndTool: 0.5,
  });

  const manpowerHours = {
    ConstructionSequence: 4,
    CandQSequence: 3,
    Documentation: 4,
    dailyTeamMeetings: 20,
    CoordinationAndTool: 20,
  };

  const handleKeyPress = (e) => {
    if (!/[0-9+]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleHours = (key, value) => {
    console.log(value);
    setSpendingHours({
      ...spendingHours,
      [key]: isNaN(parseInt(value)) ? 0 : parseInt(value),
    });
  };

  const handlePercentage = (key, value) => {
    if (!isNaN(value) && value) {
      const parsedValue = parseInt(value, 10);
      const clampedValue = Math.min(100, Math.max(0, parsedValue));

      // Convert the clamped value back to a percentage
      const percentageValue = clampedValue / 100;

      setSavingPercentage({
        ...savingPercentage,
        [key]: percentageValue,
      });
    } else {
      setSavingPercentage({
        ...savingPercentage,
        [key]: 0,
      });
    }
  };

  const handleInputChange = (key, value) => {
    console.log(value);
    setInputData({
      ...inputData,
      [key]: isNaN(parseInt(value)) ? 0 : parseInt(value),
    });
  };

  const euroFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const ovellAllCostPerMonth = useMemo(
    () =>
      inputData.deadLineLost +
      (manpowerHours.ConstructionSequence *
        inputData.coordinatorCost *
        inputData.daysInMonth +
        manpowerHours.CandQSequence *
        inputData.coordinatorCost *
        inputData.daysInMonth +
        manpowerHours.Documentation *
        inputData.coordinatorCost *
        inputData.daysInMonth +
        spendingHours.dailyTeamMeetings *
        inputData.teamMembers *
        inputData.daysInMonth *
        inputData.manpowerCost +
        spendingHours.CoordinationAndTool *
        inputData.teamMembers *
        inputData.daysInMonth *
        inputData.manpowerCost),
    [manpowerHours, inputData, spendingHours]
  );

  const savingCostPerMonth = useMemo(
    () =>
      manpowerHours.ConstructionSequence *
      inputData.coordinatorCost *
      inputData.daysInMonth *
      savingPercentage.ConstructionSequence +
      manpowerHours.CandQSequence *
      inputData.coordinatorCost *
      inputData.daysInMonth *
      savingPercentage.CandQSequence +
      manpowerHours.Documentation *
      inputData.coordinatorCost *
      inputData.daysInMonth *
      savingPercentage.Documentation +
      savingPercentage.dailyTeamMeetings *
      spendingHours.dailyTeamMeetings *
      inputData.teamMembers *
      inputData.daysInMonth *
      inputData.manpowerCost +
      savingPercentage.CoordinationAndTool *
      spendingHours.CoordinationAndTool *
      inputData.teamMembers *
      inputData.daysInMonth *
      inputData.manpowerCost,
    [manpowerHours, inputData, savingPercentage]
  );

  // piechart data

  const piedata1 = {
    labels: ["Cost savings per year	", "goPLIMS subscriptions costs per year"],
    datasets: [
      {
        label: "€",
        data: [
          savingCostPerMonth * 12,
          inputData.teamMembers * inputData.goPLIMSOLicenseCost * 12,
        ],
        backgroundColor: ["rgb(169, 209, 142)", "rgb(47, 85, 151)"],
        borderColor: ["rgb(255, 255, 255)", "rgb(255, 255, 255)"],
        borderWidth: 1,
      },
    ],
  };

  const piedata2 = {
    labels: ["Actual manpower costs per year", "Cost savings per year"],
    datasets: [
      {
        label: "€",
        data: [
          (manpowerHours.ConstructionSequence *
            inputData.coordinatorCost *
            inputData.daysInMonth +
            manpowerHours.CandQSequence *
            inputData.coordinatorCost *
            inputData.daysInMonth +
            manpowerHours.Documentation *
            inputData.coordinatorCost *
            inputData.daysInMonth +
            spendingHours.dailyTeamMeetings *
            inputData.teamMembers *
            inputData.daysInMonth *
            inputData.manpowerCost +
            spendingHours.CoordinationAndTool *
            inputData.teamMembers *
            inputData.daysInMonth *
            inputData.manpowerCost) *
          12,
          savingCostPerMonth * 12,
        ],
        backgroundColor: ["rgb(169, 209, 142)", "rgb(47, 85, 151)"],
        borderColor: ["rgb(255, 255, 255)", "rgb(255, 255, 255)"],
        borderWidth: 1,
      },
    ],
  };

  const handleConvertToPDF = async () => {
    const { value: title } = await Swal.fire({
      input: "text",
      inputLabel: "Enter reference name for the PDF",
      inputPlaceholder: "Reference Name",
    });

    // Display the Swal alert before generating the PDF
    Swal.fire({
      title: "Generating PDF",
      text: "Please wait while we create the PDF...",
      allowOutsideClick: false,
      showCancelButton: false,
      showConfirmButton: false,
      onBeforeOpen: () => {
        Swal.showLoading();
      },
    });

    const content = contentRef.current;

    const tipsAndButton = content.querySelector(".sub-tipMenu");
    tipsAndButton.style.display = "none";
    const strippedElements = content.querySelectorAll(".stripped");

    strippedElements.forEach((element) => {
      element.style.backgroundColor = "transparent";
    });

    if (window.innerWidth < 1024) {
      var vp = document.getElementById("viewportMeta").getAttribute("content");
      document
        .getElementById("viewportMeta")
        .setAttribute("content", "width=1366");
    }

    const pdf = new jsPDF({ compress: true });

    pdf.setFontSize(8);
    pdf.text(title, 170, 5);

    html2canvas(content, { scale: 1 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png", 0.1);

      tipsAndButton.style.display = "flex";
      strippedElements.forEach((element) => {
        element.style.backgroundColor = "#f2f2f2";
      });

      pdf.addImage(imgData, "PNG", 10, 10, 190, 0);
      pdf.save("goplims_savingsCalculator.pdf");

      Swal.close();

      if (vp) {
        document.getElementById("viewportMeta").setAttribute("content", vp);
      }
    });
  };

  console.log(
    "ovellAllCostPerMonth:::::::::",
    ovellAllCostPerMonth,
    savingCostPerMonth
  );
  return (
    <Container fluid ref={contentRef}>
      <Row className="my-2 header-part">
        <Col xs={3} md={2} lg={2}>
          <img className="plimsologo" src={plimsologo} alt="plimsoLogo" />
        </Col>
        <Col xs={12} md={12} lg={8} className="top-header text-center">
          <p>MANAGING TEAMS - COST ESTIMATES</p>
          <p>
            POTENTIAL SAVINGS CALCULATOR & <br />
            ROI CALCULATOR
          </p>
          <p>
            (Estimated percentage time and cost savings on using goPLIMS Work
            Management Platform)
          </p>
        </Col>
      </Row>
      <Row className="justify-content-start sub-tipMenu">
        <Col xs={11} md={5} lg={5} className="tip-edit ">
          TIP: Enter your data/details in the red coloured field to check how
          much you can save.
        </Col>
        <Col xs={12} md={6} lg={6} className="">
          <div className="button-container">
            <button className="button btn-blue m-3">Contact</button>
            <button
              className="button btn-green m-3"
              onClick={handleConvertToPDF}
            >
              Download as pdf
            </button>
          </div>
        </Col>
      </Row>
      <div id="charttable">
        <div className="table-parent text-center">
          <table className="excel-table">
            <thead>
              <tr className="highlight">
                <th className="header-editable">Number of Team Members</th>
                <th className="overall-editable">
                  <input
                    className="input-header"
                    value={inputData.teamMembers}
                    type="text"
                    onChange={(e) =>
                      handleInputChange("teamMembers", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </th>
                <th className="header-editable">
                  Number of working days in a month
                </th>
                <th className="overall-editable">
                  <input
                    className="input-header"
                    value={inputData.daysInMonth}
                    type="text"
                    onChange={(e) =>
                      handleInputChange("daysInMonth", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </th>
                <th className="header-editable">
                  Average Coordinator or admin cost / hour (Estimated)
                </th>
                <th className="overall-editable  currency">
                  <input
                    className="input-header"
                    value={inputData.coordinatorCost}
                    type="text"
                    onChange={(e) =>
                      handleInputChange("coordinatorCost", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </th>
                <th className="header-editable">
                  Average manpower cost for the team
                </th>
                <th className="overall-editable currency">
                  <input
                    className="input-header"
                    value={inputData.manpowerCost}
                    type="text"
                    onChange={(e) =>
                      handleInputChange("manpowerCost", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </th>
                <th className="header-editable">
                  goPLIMS license Subscription per  user / month
                </th>
                <th className="overall-editable currency">
                  <input
                    className="input-header"
                    value={inputData.goPLIMSOLicenseCost}
                    type="text"
                    onChange={(e) =>
                      handleInputChange("goPLIMSOLicenseCost", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </th>
                <th className="header-editable">Estimated number of meeting per day/ team member</th>
                <th className="overall-editable">
                  <input
                    className="input-header"
                    value={spendingHours.dailyTeamMeetings}
                    type="text"
                    onChange={(e) =>
                      handleHours("dailyTeamMeetings", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </th>

              </tr>
              <tr className="header-two">
                <th colSpan={5}>Project / Workstream Activities</th>
                <th>Number of hours spent /day</th>
                <th>Estimated hours spent/day(Man-hour)</th>
                <th>Cost per month</th>
                <th>Estimated Percentage Savings using goPLIMS</th>
                <th>Man-hour Reduction in hours/week</th>
                <th>Savings per month</th>
                <th>goPLIMS License cost per month </th>
              </tr>
            </thead>
            <tbody className="font-table ">
              <tr>
                <td colSpan={5} className="left-header lightblue-bg">
                  1) Time spent on meeting coordination and approvals of
                  documentations
                </td>
                <td className="small-editable">
                  <input
                    value={spendingHours.ConstructionSequence}
                    type="text"
                    onChange={(e) =>
                      handleHours("ConstructionSequence", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>{Math.round(manpowerHours.ConstructionSequence)}</td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      manpowerHours.ConstructionSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth
                    )
                  )}
                </td>
                <td className="small-editable percentage">
                  <input
                    value={savingPercentage.ConstructionSequence * 100}
                    type="text"
                    onChange={(e) =>
                      handlePercentage("ConstructionSequence", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>
                  {(
                    savingPercentage.ConstructionSequence *
                    manpowerHours.ConstructionSequence
                  ).toFixed(1)}
                </td>
                <td>
                  {euroFormatter.format(
                    // Math.round
                    //    manpowerHours.ConstructionSequence
                    //    inputData.coordinatorCost
                    //     inputData.daysInMonth
                    //     savingPercentage.ConstructionSequence
                    //       manpowerHours.ConstructionSequence
                    Math.round(
                      manpowerHours.ConstructionSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth *
                      savingPercentage.ConstructionSequence
                    )
                  )}
                </td>
                <td rowSpan={6}>
                  {euroFormatter.format(
                    Math.round(
                      inputData.teamMembers * inputData.goPLIMSOLicenseCost
                    )
                  )}
                </td>
              </tr>
              <tr className="stripped">
                <td colSpan={5} className="left-header orange-bg">
                  2) Time spent on coordinating between teams to get delegate
                  work or receive updates
                </td>
                <td className="small-editable">
                  <input
                    value={spendingHours.CandQSequence}
                    type="text"
                    onChange={(e) =>
                      handleHours("CandQSequence", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>{Math.round(manpowerHours.CandQSequence)}</td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      manpowerHours.CandQSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth
                    )
                  )}
                </td>
                <td className="small-editable percentage">
                  <input
                    value={savingPercentage.CandQSequence * 100}
                    type="text"
                    onChange={(e) =>
                      handlePercentage("CandQSequence", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>
                  {(
                    savingPercentage.CandQSequence * manpowerHours.CandQSequence
                  ).toFixed(1)}
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      manpowerHours.CandQSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth *
                      savingPercentage.CandQSequence
                    )
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={5} className="left-header lightblue-bg">
                  3) Time spent on preparing meeting minutes, formatting &
                  emailing
                </td>
                <td className="small-editable">
                  <input
                    value={spendingHours.Documentation}
                    type="text"
                    onChange={(e) =>
                      handleHours("Documentation", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>{Math.round(manpowerHours.Documentation)}</td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      manpowerHours.Documentation *
                      inputData.coordinatorCost *
                      inputData.daysInMonth
                    )
                  )}
                </td>
                <td className="small-editable percentage">
                  <input
                    value={savingPercentage.Documentation * 100}
                    type="text"
                    onChange={(e) =>
                      handlePercentage("Documentation", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>
                  {(
                    savingPercentage.Documentation * manpowerHours.Documentation
                  ).toFixed(1)}
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      manpowerHours.Documentation *
                      inputData.coordinatorCost *
                      inputData.daysInMonth *
                      savingPercentage.Documentation
                    )
                  )}
                </td>
              </tr>
              <tr className="stripped">
                <td colSpan={5} className="left-header orange-bg">
                  4) Estimated time spent by a team member on the team meetings
                  per day
                </td>
                <td className="small-editable">
                  <input
                    value={spendingHours.dailyTeamMeetings}
                    type="text"
                    onChange={(e) =>
                      handleHours("dailyTeamMeetings", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>
                  {" "}
                  {Math.round(
                    spendingHours.dailyTeamMeetings * inputData.teamMembers
                  )}
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      spendingHours.dailyTeamMeetings *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost
                    )
                  )}
                </td>
                <td className="small-editable percentage">
                  <input
                    value={savingPercentage.dailyTeamMeetings * 100}
                    type="text"
                    onChange={(e) =>
                      handlePercentage("dailyTeamMeetings", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>
                  {(
                    savingPercentage.dailyTeamMeetings *
                    spendingHours.dailyTeamMeetings *
                    inputData.teamMembers
                  ).toFixed(1)}
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      savingPercentage.dailyTeamMeetings *
                      spendingHours.dailyTeamMeetings *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost
                    )
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={5} className="left-header lightblue-bg">
                  5) Time spent on searching for details as information is
                  scattered in multiple locations or time lost due to working on
                  incorrect versions
                </td>
                <td className="small-editable">
                  <input
                    value={spendingHours.CoordinationAndTool}
                    type="text"
                    onChange={(e) =>
                      handleHours("CoordinationAndTool", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>
                  {Math.round(
                    spendingHours.CoordinationAndTool * inputData.teamMembers
                  )}
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      spendingHours.CoordinationAndTool *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost
                    )
                  )}
                </td>
                <td className="small-editable percentage">
                  <input
                    value={savingPercentage.CoordinationAndTool * 100}
                    type="text"
                    onChange={(e) =>
                      handlePercentage("CoordinationAndTool", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                  />
                </td>
                <td>
                  {(
                    savingPercentage.CoordinationAndTool *
                    spendingHours.CoordinationAndTool *
                    inputData.teamMembers
                  ).toFixed(1)}
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      savingPercentage.CoordinationAndTool *
                      spendingHours.CoordinationAndTool *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost
                    )
                  )}
                </td>
              </tr>
              <tr rowSpan={2}>
                <td colSpan={6} className="chartHeader">
                  For more details on goPLIMS package <br />
                  Visit:{" "}
                  <a
                    href="https://goplims.com"
                    style={{ color: "white", textDecoration: "none" }}
                  >
                    https://goplims.com
                  </a>{" "}
                  or contact us at Team@goplims.com
                </td>
                <td>
                  <b>Total Manpower costs / month</b>
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      manpowerHours.ConstructionSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth +
                      manpowerHours.CandQSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth +
                      manpowerHours.Documentation *
                      inputData.coordinatorCost *
                      inputData.daysInMonth +
                      spendingHours.dailyTeamMeetings *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost +
                      spendingHours.CoordinationAndTool *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost
                    )
                  )}
                </td>
                <td className="ash-blank"></td>
                <td>
                  <b>Estimated Savings / month</b>
                </td>
                <td>
                  {euroFormatter.format(
                    Math.round(
                      manpowerHours.ConstructionSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth *
                      savingPercentage.ConstructionSequence +
                      manpowerHours.CandQSequence *
                      inputData.coordinatorCost *
                      inputData.daysInMonth *
                      savingPercentage.CandQSequence +
                      manpowerHours.Documentation *
                      inputData.coordinatorCost *
                      inputData.daysInMonth *
                      savingPercentage.Documentation +
                      savingPercentage.dailyTeamMeetings *
                      spendingHours.dailyTeamMeetings *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost +
                      savingPercentage.CoordinationAndTool *
                      spendingHours.CoordinationAndTool *
                      inputData.teamMembers *
                      inputData.daysInMonth *
                      inputData.manpowerCost
                    )
                  )}
                </td>
              </tr>
            </tbody>
            <tbody className="font-table sub-table">
              <tr>
                <td colSpan={3} className="sub-table-heading">
                  Summary of Cost Savings Vs Subscription costs
                </td>
                <td colSpan={3} className="sub-table-heading">
                  goPLIMS subscription cost Vs Savings
                </td>
                <td colSpan={3} className="sub-table-heading">
                  Summary of Cost Savings Vs Subscription costs
                </td>
                <td colSpan={3} className="sub-table-heading">
                  Estimate Cost Savings / Year
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="blue-bg">
                  Cost savings per year
                </td>
                <td colSpan={1} className="lightgreen-bg">
                  {euroFormatter.format(Math.round(savingCostPerMonth * 12))}
                </td>
                <td colSpan={3} rowSpan={3} className="">
                  <div className="piechart-report">
                    <Pie data={piedata1} />
                  </div>
                </td>
                <td colSpan={2} className="blue-bg">
                  Actual manpower costs per year
                </td>
                <td colSpan={1}>
                  {euroFormatter.format(
                    Math.round(
                      (manpowerHours.ConstructionSequence *
                        inputData.coordinatorCost *
                        inputData.daysInMonth +
                        manpowerHours.CandQSequence *
                        inputData.coordinatorCost *
                        inputData.daysInMonth +
                        manpowerHours.Documentation *
                        inputData.coordinatorCost *
                        inputData.daysInMonth +
                        spendingHours.dailyTeamMeetings *
                        inputData.teamMembers *
                        inputData.daysInMonth *
                        inputData.manpowerCost +
                        spendingHours.CoordinationAndTool *
                        inputData.teamMembers *
                        inputData.daysInMonth *
                        inputData.manpowerCost) *
                      12
                    )
                  )}
                </td>
                <td colSpan={3} rowSpan={3} className="">
                  <div className="piechart-report">
                    <Pie data={piedata2} />
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="blue-bg">
                  goPLIMS subscriptions costs per year
                </td>
                <td colSpan={1} className="lightgreen-bg">
                  {euroFormatter.format(
                    Math.round(
                      inputData.teamMembers * inputData.goPLIMSOLicenseCost * 12
                    )
                  )}
                </td>
                <td colSpan={2} className="blue-bg">
                  Cost savings per year
                </td>
                <td colSpan={1}>
                  {euroFormatter.format(Math.round(savingCostPerMonth * 12))}
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="blue-bg">
                  % cost spent in subscription for goPLIMS package Vs cost
                  savings
                </td>
                <td colSpan={1} className="bg-darkgreen">
                  {(
                    ((inputData.teamMembers *
                      inputData.goPLIMSOLicenseCost *
                      12) /
                      (savingCostPerMonth * 12)) *
                    100
                  ).toFixed(1) + "%"}
                </td>
                <td colSpan={2} className="blue-bg">
                  Average percentage savings from using goPLIMS platform
                </td>
                <td colSpan={1}>
                  {(
                    ((savingCostPerMonth * 12) /
                      ((manpowerHours.ConstructionSequence *
                        inputData.coordinatorCost *
                        inputData.daysInMonth +
                        manpowerHours.CandQSequence *
                        inputData.coordinatorCost *
                        inputData.daysInMonth +
                        manpowerHours.Documentation *
                        inputData.coordinatorCost *
                        inputData.daysInMonth +
                        spendingHours.dailyTeamMeetings *
                        inputData.teamMembers *
                        inputData.daysInMonth *
                        inputData.manpowerCost +
                        spendingHours.CoordinationAndTool *
                        inputData.teamMembers *
                        inputData.daysInMonth *
                        inputData.manpowerCost) *
                        12)) *
                    100
                  ).toFixed(1) + "%"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <Row className="purpose">
        <Col xs={12} md={4} lg={6} className="mb-3">
          <div className="voilet-bg">
            INTERESTED TO KNOW HOW MUCH YOU CAN SAVE? <br />
            Enter your actuals and estimate your savings
          </div>
          <div className="save-tech">
            <p className="lightblue-bg">
              1) All coordinations and approvals can be managed online
              efficiently under each respective topic
            </p>
            <p className="orange-bg">
              1) All team members are empowered to centrally update 24 X 7{" "}
              <br />
              2) The platform supports multi-lingual teams to collaborate{" "}
            </p>
            <p className="lightblue-bg">
              1) goPLIMS package does automated formatting of meeting minutes
              <br />
              2) All updates to each action is automatically added to meeting
              minutes
            </p>
            <p className="orange-bg">
              1) All actions are updated in the central register
              <br />
              2) Meeting minutes are auto formatted and emailed
            </p>
            <p className="lightblue-bg">
              1) All information is maintained centrally to avoid searching
              <br />
              2) All updates are maintained topic wise to minimise track and
              trace
            </p>
            <p className="orange-bg">
              Note: goPLIMS provides a WYSIWYG standardised look and feel to all
              users{" "}
            </p>
          </div>
        </Col>
        <Col xs={12} md={4} lg={6} className="unique-goplims">
          <span className="fw-bold">What's unique about goPLIMS platform</span>
          <ol type="a">
            <li>
              Empower global teams to collaborate in native language
              (multi-lingual)
            </li>
            <li>
              Eliminate subjective interpretation of actions and meeting minutes
            </li>
            <li>
              Prevent record manipulations with inbuilt data integrity &
              security
            </li>
            <li>
              Automate delivery of meeting minutes by email consistently to the
              team
            </li>
            <li>
              Delivery of the information to the right resources; prevent
              inadvertent publishing of any information to unauthorised
              stakeholders
            </li>
            <li>Limit access of information to authorised users only</li>
          </ol>
        </Col>

      </Row>
    </Container>
  );
};

export default DataTable;
